
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Location } from '@/types/dashboard';

interface LocationContextType {
  currentLocation: Location | null;
  availableLocations: Location[];
  setCurrentLocation: (location: Location) => void;
  isLoading: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentLocation, setCurrentLocationState] = useState<Location | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('name');

      if (error) throw error;

      setAvailableLocations(data || []);
      
      // Set first location as default if none selected
      if (data && data.length > 0 && !currentLocation) {
        setCurrentLocationState(data[0]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setCurrentLocation = (location: Location) => {
    setCurrentLocationState(location);
    // For now, skip saving to preferences since the table doesn't exist yet
    // saveLocationPreference(location);
  };

  // const saveLocationPreference = async (location: Location) => {
  //   try {
  //     const { error } = await supabase
  //       .from('staff_preferences')
  //       .upsert({
  //         preference_type: 'default_location',
  //         preference_value: { location_id: location.id }
  //       });

  //     if (error) throw error;
  //   } catch (error) {
  //     console.error('Error saving location preference:', error);
  //   }
  // };

  return (
    <LocationContext.Provider value={{
      currentLocation,
      availableLocations,
      setCurrentLocation,
      isLoading
    }}>
      {children}
    </LocationContext.Provider>
  );
};
